import type { ReactNode } from 'react';
import type { AgentTokenPublic } from 'types';
import type { AgentTokenScope } from 'definitions/agent-tokens';
import type { AgentTokenOwner } from '@/state/useAgentTokens';
import { cn } from 'helpers/cn';
import { formatDate, formatDateTime } from 'helpers/formatters';
import { DataTable, useDataControl } from '@/components/DataTable';
import { useIsMobile } from '@/components/ui/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ActionButton } from '../../ConsolePanel';
import { AGENT_ACCESS_COPY, SCOPE_COPY } from './agent-access-copy';
import { tokenStatus } from './use-agent-access';

const COPY = AGENT_ACCESS_COPY;

// The status chip of the team list, Settings, Team.
const CHIP = 'micro-label rounded-pill inline-flex w-fit border px-2 py-0.5';

const STATUS_TEXT = { active: COPY.active, expired: COPY.expired, revoked: COPY.revoked } as const;

const statusChip = (status: keyof typeof STATUS_TEXT) =>
  status === 'active' ? 'border-ok-line text-ok' : 'border-line text-dim';

type TableToken = AgentTokenPublic & { owner?: AgentTokenOwner };

type TokensTableProps = {
  tokens: readonly TableToken[];
  now: number;
  /** The list's accessible name. */
  label: string;
  emptyText: string;
  /** Under the empty text: the action that makes the first one. */
  emptyAction?: ReactNode;
  /** Adds the "Made by" column, for the team list. */
  showOwner?: boolean;
  /** To name a token's websites in the Access tooltip. An id not listed here is only counted. */
  websites?: readonly { id: number; url: string }[];
  onRevoke: (token: AgentTokenPublic) => void;
};

const ownerName = (owner: AgentTokenOwner | undefined) =>
  owner ? [owner.firstName, owner.lastName].filter(Boolean).join(' ') || owner.email : '';

/** How many scopes the Access cell names before it counts the rest. */
const SCOPES_SHOWN = 2;

/** "Read, Build +3": the scopes in short form, for a cell. The tooltip has them in full. */
export const scopeSummary = (scopes: readonly AgentTokenScope[]): string => {
  const named = scopes.slice(0, SCOPES_SHOWN).map((scope) => SCOPE_COPY[scope]?.short ?? scope);
  const rest = scopes.length - named.length;
  return rest > 0 ? `${named.join(', ')} +${rest}` : named.join(', ');
};

type ReachToken = Pick<AgentTokenPublic, 'websiteIds' | 'companyIds'>;

/** "All websites", or how many websites and accounts it is limited to. */
export const reachSummary = ({ websiteIds, companyIds }: ReachToken): string => {
  const parts = [
    ...(websiteIds ? [COPY.websiteCount(websiteIds.length)] : []),
    ...(companyIds ? [COPY.companyCount(companyIds.length)] : []),
  ];
  return parts.length ? parts.join(', ') : COPY.allWebsitesShort;
};

/** A connected app (PER-334) is named by its host; a personal token by the start of its secret. */
const isApp = (token: TableToken) => token.kind === 'oauth';

/**
 * A connected app's host, from the client ID it was connected under. Read from there, in full,
 * because the host is the one thing about a client we have proved, and the stored prefix is cut to
 * its column: `claude.ai.attacker.example` must never read as `claude.ai.attack`.
 */
export const appHost = (token: Pick<AgentTokenPublic, 'clientId' | 'prefix'>): string =>
  URL.parse(token.clientId ?? '')?.host ?? token.prefix;

/** How many websites the Access tooltip names before it counts the rest. */
const WEBSITES_SHOWN = 8;

type WebsiteDetailParams = { ids: readonly number[]; urlById: ReadonlyMap<number, string> };

/** The first few websites by name, and how many more: a token can name thousands. */
export const websiteDetail = ({ ids, urlById }: WebsiteDetailParams) => {
  const urls = ids.slice(0, WEBSITES_SHOWN).flatMap((id) => urlById.get(id) ?? []);
  return { urls, rest: ids.length - urls.length };
};

type AccessCellProps = { token: TableToken; urlById: ReadonlyMap<number, string> };

/**
 * What the token may do and where, short enough for a cell, with the whole of it in a tooltip. A
 * button, so the detail is reachable by keyboard. A Radix tooltip does not open on a tap, so a phone
 * card, which has the room, lists the scopes in full instead (useIsMobile is what DataTable switches
 * to cards on).
 */
const AccessCell = ({ token, urlById }: AccessCellProps) => {
  const isMobile = useIsMobile();
  const { urls, rest } = websiteDetail({ ids: token.websiteIds ?? [], urlById });

  if (isMobile) {
    return (
      <span className="grid">
        {token.scopes.map((scope) => (
          <span key={scope} className="text-foreground">
            {SCOPE_COPY[scope]?.label ?? scope}
          </span>
        ))}
        <span className="supporting">{reachSummary(token)}</span>
      </span>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${COPY.accessDetails} ${token.name}`}
            className="focus-visible:ring-ring grid max-w-full rounded-sm text-left focus-visible:ring-1 focus-visible:outline-none"
          >
            <span className="text-foreground truncate">{scopeSummary(token.scopes)}</span>
            <span className="supporting truncate">{reachSummary(token)}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="border-line bg-panel text-foreground rounded-lg">
          <div className="supporting grid max-w-xs gap-1.5">
            <ul className="m-0 list-none p-0">
              {token.scopes.map((scope) => (
                <li key={scope}>{SCOPE_COPY[scope]?.label ?? scope}</li>
              ))}
            </ul>
            <ul className="m-0 list-none p-0">
              {urls.map((url) => (
                <li key={url}>{url}</li>
              ))}
              {rest > 0 && <li>{urls.length ? COPY.moreWebsites(rest) : COPY.websiteCount(rest)}</li>}
              {token.companyIds && <li>{COPY.companyCount(token.companyIds.length)}</li>}
              {!token.websiteIds && !token.companyIds && <li>{COPY.allWebsitesShort}</li>}
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * A list of agent tokens, as the team list in Settings, Team lays out its members: the same
 * DataTable, a chip for the status, the action last, and cards on a phone from the same columns.
 * Revoked and expired tokens stay listed, dimmed, so the history of what agents had access is
 * visible. The start of the secret sits under the name, which is how a person recognises one; a
 * connected app has its host there, and says Disconnect where a token says Revoke.
 */
export const TokensTable = ({
  tokens,
  now,
  label,
  emptyText,
  emptyAction,
  showOwner = false,
  websites,
  onRevoke,
}: TokensTableProps) => {
  const dataController = useDataControl<TableToken>(tokens as TableToken[]);
  const urlById = new Map(websites?.map((website) => [website.id, website.url]));

  /** A row is dimmed cell by cell: the table has no row class, and the cards share these cells. */
  const dimmed = (token: TableToken) => cn('block min-w-0', tokenStatus({ token, now }) !== 'active' && 'opacity-60');

  return (
    <div role="region" aria-label={label}>
      <DataTable<TableToken>
        dataController={dataController}
        pageSize={10}
        noDataMessage={emptyText}
        noDataCTA={emptyAction}
        columns={[
          {
            field: 'name',
            label: COPY.columns.name,
            sortKey: 'name',
            headerClassName: 'micro-label',
            cellClassName: 'min-w-0',
            mobile: 'title',
            format: ({ item }) => (
              <span className={dimmed(item)}>
                <span className="text-foreground block truncate font-medium" title={item.name}>
                  {item.name}
                </span>
                {isApp(item) ? (
                  <span className="text-dim block truncate text-meta" title={appHost(item)}>
                    {appHost(item)}
                  </span>
                ) : (
                  <span className="text-dim block truncate font-mono text-micro" title={COPY.columns.prefix}>
                    {item.prefix}…
                  </span>
                )}
              </span>
            ),
          },
          {
            label: COPY.columns.owner,
            headerClassName: 'micro-label',
            condition: showOwner,
            mobile: 'meta',
            format: ({ item }) => (
              <span className={cn(dimmed(item), 'text-dim truncate')}>{ownerName(item.owner)}</span>
            ),
          },
          {
            label: COPY.columns.access,
            headerClassName: 'micro-label',
            cellClassName: 'min-w-0',
            mobile: 'meta',
            format: ({ item }) => (
              <span className={dimmed(item)}>
                <AccessCell token={item} urlById={urlById} />
              </span>
            ),
          },
          {
            field: 'lastUsedAt',
            label: COPY.columns.lastUsed,
            sortKey: 'lastUsedAt',
            headerClassName: 'micro-label',
            mobile: 'meta',
            format: ({ item }) => (
              <span className={cn(dimmed(item), 'num text-dim')}>
                {item.lastUsedAt ? formatDateTime(item.lastUsedAt) : COPY.never}
              </span>
            ),
          },
          {
            field: 'expiresAt',
            label: COPY.columns.expires,
            sortKey: 'expiresAt',
            headerClassName: 'micro-label',
            mobile: 'meta',
            format: ({ item }) => (
              <span className={cn(dimmed(item), 'num text-dim')}>{formatDate(item.expiresAt)}</span>
            ),
          },
          {
            label: COPY.columns.status,
            headerClassName: 'micro-label w-28',
            mobile: 'meta',
            format: ({ item }) => {
              const status = tokenStatus({ token: item, now });
              return (
                <span className={dimmed(item)}>
                  <span className={cn(CHIP, statusChip(status))}>{STATUS_TEXT[status]}</span>
                </span>
              );
            },
          },
          {
            label: COPY.columns.actions,
            headerClassName: 'micro-label w-20',
            mobile: 'action',
            format: ({ item }) =>
              tokenStatus({ token: item, now }) === 'active' ? (
                <ActionButton
                  onClick={() => onRevoke(item)}
                  ariaLabel={`${isApp(item) ? COPY.disconnect : COPY.revoke} ${item.name}`}
                  className="text-danger"
                >
                  {isApp(item) ? COPY.disconnect : COPY.revoke}
                </ActionButton>
              ) : null,
          },
        ]}
      />
    </div>
  );
};
